import type { EventLogger } from '../experiments/EventLogger.ts';
import type { SimulatedEmgDevice } from '../hardware/SimulatedEmgDevice.ts';
import type { VirtualButtonDevice } from '../hardware/VirtualButtonDevice.ts';
import type { SimulatedHumanStimulator } from '../stimulation/SimulatedHumanStimulator.ts';
import type { StimulationService } from '../stimulation/StimulationService.ts';
import type { BotController } from './BotController.ts';
import {
  StimulationController,
  type StimulationControllerOptions,
} from './StimulationController.ts';

/**
 * `StimulationController` wired to a simulated participant.
 *
 * There is almost nothing here, and that is the point: developing against a
 * synthetic actuator must exercise the *same* controller that will later drive
 * a real one. This subclass only relabels the mode and keeps typed references
 * to the simulated pieces so the dashboard can show the response model's
 * parameters and counters. Every decision, request, attribution, timeout, and
 * flap is handled by the parent class.
 *
 * The one thing worth noticing is what is NOT here: no shortcut from the bot's
 * decision to the game. The simulated participant presses a
 * `VirtualButtonDevice`, and this controller finds out about it through the
 * ordinary `InputDevice` subscription, exactly as it would with a real switch.
 */
export class SimulatedStimulationController extends StimulationController {
  constructor(
    bot: BotController,
    service: StimulationService,
    readonly button: VirtualButtonDevice,
    readonly human: SimulatedHumanStimulator,
    logger: EventLogger,
    getTick: () => number,
    options: Partial<StimulationControllerOptions> = {},
    readonly emg?: SimulatedEmgDevice,
  ) {
    super(
      bot,
      service,
      button,
      logger,
      getTick,
      options,
      'simulated-stimulation',
      'Bot → simulated human → virtual button',
    );
  }

  override status(): Record<string, unknown> {
    return {
      ...super.status(),
      model: {
        meanLatencyMs: this.human.model.meanLatencyMs,
        latencyStdDevMs: this.human.model.latencyStdDevMs,
        successProbability: this.human.model.successProbability,
        falsePositiveProbability: this.human.model.falsePositiveProbability,
        refractoryPeriodMs: this.human.model.refractoryPeriodMs,
      },
      simulatedParticipant: this.human.counters,
    };
  }
}
